import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {SpecialPower} from '../special-power.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {Statistics} from '../statistics.js';
import {Puzzle} from '../puzzle.js';
import {CameraShakeParameters} from '../../classes/CameraShakeParameters.js';
import {BoardObject} from '../board-object.js';
import {CoroutineManager} from '../../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../../classes/coroutines/Coroutine.js';
import {waitForSeconds} from '../../classes/coroutines/YieldInstructions.js';
import {Sounds} from '../../enums/Sounds.js';
import {AudioDirector} from '../audio-director.js';

export enum BombType {
    ColorMatch,
    Radial,
}

export class BombPower extends SpecialPower {
    static TypeName = 'bomb-power';
    static InheritProperties = true;

    @property.enum(
        Object.keys(BombType).filter((b) => isNaN(Number(b))),
        BombType.ColorMatch
    )
    bombType: BombType = BombType.ColorMatch;

    cameraShakeParameters: CameraShakeParameters = new CameraShakeParameters(
        4.5,
        2,
        0.067,
        8
    );

    @property.int(3)
    explosionRadius = 3;

    @property.int(3)
    turnsToExplode = 3;

    @property.float(0.5)
    initialBlinkSpeedInSeconds = 0.5;

    @property.color(1.0, 1.0, 1.0, 1.0)
    blinkColor1 = [1.0, 1.0, 1.0, 1.0];

    @property.color(1.0, 1.0, 1.0, 1.0)
    blinkColor2 = [1.0, 1.0, 1.0, 1.0];

    @property.int(0)
    explosionRadiusIncrease2 = 0;
    @property.int(1)
    explosionRadiusIncrease3 = 1;
    @property.int(2)
    explosionRadiusIncrease4 = 2;
    @property.int(3)
    explosionRadiusIncrease5 = 3;

    @property.enum(Object.keys(TransactionalNames).filter((b) => isNaN(Number(b))))
    stat: TransactionalNames;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    explodeSFX: Sounds;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    warningSFX: Sounds;

    private _upgradeLevel: number;
    private _newExplosionRadius: number;
    private _exploded: boolean;
    private _loaded: boolean;
    private _boardObject: BoardObject;
    private _activating: any;
    private _coroutineManager = new CoroutineManager();
    private _explodeAfterTimeId: number = 0;

    start(): void {
        this._upgradeLevel = Statistics.instance.getTransactional(this.stat);

        switch (this._upgradeLevel) {
            case 1:
                this._newExplosionRadius = this.explosionRadius;
                break;
            case 2:
                this._newExplosionRadius =
                    this.explosionRadius + this.explosionRadiusIncrease2;
                break;
            case 3:
                this._newExplosionRadius =
                    this.explosionRadius + this.explosionRadiusIncrease3;
                break;
            case 4:
                this._newExplosionRadius =
                    this.explosionRadius + this.explosionRadiusIncrease4;
                break;
            case 5:
                this._newExplosionRadius =
                    this.explosionRadius + this.explosionRadiusIncrease5;
                break;
            default:
                this._newExplosionRadius = this.explosionRadius;
                break;
        }

        // AudioSource source = GetComponent<AudioSource>();
        // if (source != null)
        // {
        // 	isLooping = source.loop;
        // 	if (isLooping)
        // 	{
        // 		AudioDirector.GetDirector().PlayLooping(source.clip, source.volume, source.priority);
        // 	}
        // 	else
        // 	{
        // 		AudioDirector.GetDirector().PlayOneShot(source.clip, source, true);
        // 	}
        // }
    }

    update(dt: number): void {
        this._coroutineManager.update(dt);
    }

    onDeactivate(): void {
        // if (isLooping)
        // {
        // 	AudioSource source = GetComponent<AudioSource>();
        // 	if (source != null && source.loop)
        // 	{
        // 		AudioDirector.GetDirector().StopLooping(source.clip);
        // 	}
        // }
    }

    override activate(puzzle: Puzzle, row: number, column: number): void {
        // do not call super, because there's no activation
        if (this._exploded) {
            return;
        }
        this._exploded = true;

        if (!this._loaded && this.bombType == BombType.ColorMatch) {
            this.bombType = BombType.Radial;
        }

        switch (this.bombType) {
            case BombType.Radial:
                puzzle.explodeRadial(row, column, this._newExplosionRadius);
                break;
            case BombType.ColorMatch:
                puzzle.explodeAllOfColor(row, column);
                break;
        }

        puzzle.mainCamera.shakeCamera(this.cameraShakeParameters);

        // if (isLooping)
        // {
        //     AudioSource source = GetComponent<AudioSource>();
        //     if (source != null)
        //     {
        //         AudioDirector.GetDirector().StopLooping(source.clip);
        //     }
        // }

        // if (explodeSFX)
        // {
        AudioDirector.instance.playOneShotWithPriority(this.explodeSFX);
        // }
    }

    override loaded(newParent: BoardObject): void {
        // called when this object is loaded into another
        this._loaded = true;
        this._boardObject = newParent;
        if (!this._activating) {
            this._boardObject.puzzle.registerForActivation(
                this._boardObject.row,
                this._boardObject.column
            );
            this._activating = true;
        }
    }

    override placed(puzzle: Puzzle, newParent: BoardObject): void {
        if (puzzle.rules.isCoconutEmergency()) {
            this._boardObject = newParent;
            this._boardObject.puzzle.registerForActivation(
                this._boardObject.row,
                this._boardObject.column
            );
        } else if (this._explodeAfterTimeId != 0) {
            this._boardObject = newParent;
            this._explodeAfterTimeId = this._coroutineManager.addCoroutine(
                new Coroutine(this._explodeAfterTime())
            );
        }
    }

    private *_explodeAfterTime() {
        if (this._activating) {
            // break out of coroutine
            return;
        }

        const initialTurn = this._boardObject.puzzle.rules.turnsSinceStart;

        // Material material = bombRenderer.material;
        // Color originalColor = material.color;
        // Color blink1 = blinkColor1;
        // blink1.a = originalColor.a;
        // Color blink2 = blinkColor2;
        // blink2.a = originalColor.a;

        let remainingTurns = this.turnsToExplode;
        while (remainingTurns > 0) {
            const currentTurn = this._boardObject.puzzle.rules.turnsSinceStart;
            const turnsElapsed = currentTurn - initialTurn;
            // if (Time.timeScale > 0.0f)
            // {
            remainingTurns = this.turnsToExplode - turnsElapsed;
            // }

            if (remainingTurns > 0 && remainingTurns <= this.turnsToExplode) {
                const blinkTimeFactor =
                    remainingTurns * (this.initialBlinkSpeedInSeconds / 2.0);

                // 		material.color = blink1;
                yield* waitForSeconds(blinkTimeFactor);
                if (this._activating) {
                    return;
                }
                if (currentTurn != this._boardObject.puzzle.rules.turnsSinceStart) {
                    continue;
                }
                // 		if (warningSFX && Time.timeScale > 0.0f)
                /// 		{
                AudioDirector.instance.playOneShot(this.warningSFX, true);
                // 		}
                // 		// blink color off
                // 		material.color = blink2;
                yield* waitForSeconds(blinkTimeFactor);
                if (this._activating) {
                    return;
                }
            }

            yield null;
        }

        if (this._boardObject && !this._activating) {
            this._boardObject.puzzle.registerForActivation(
                this._boardObject.row,
                this._boardObject.column
            );
            this._activating = true;
        }
    }
}
